import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { RootState } from '@/store/store';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetCommunityEventsQuery,
  useDeleteEventMutation,
} from '@/services/CommunityManagementService';
import { IEventData, ICommunityData } from '@/types/community-management.types';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { usePermissions } from '@/hooks';
import { formatDisplayDate } from '@/utils/Date';

interface EventPromotionTabProps {
  community: ICommunityData | null;
}

const getStatusBadge = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return <span className="badge badge-success">Active</span>;
    case 'DRAFT':
      return <span className="badge badge-secondary">Draft</span>;
    case 'CANCELLED':
      return <span className="badge badge-danger">Cancelled</span>;
    case 'COMPLETED':
      return <span className="badge badge-primary">Completed</span>;
    default:
      return <span className="badge badge-secondary">{status}</span>;
  }
};

const formatDate = (dateString: string) => {
  try {
    return formatDisplayDate(new Date(dateString));
  } catch {
    return dateString;
  }
};

const EventPromotion: React.FC<EventPromotionTabProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<IEventData | null>(null);

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
      community_id: communityId || '',
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue, communityId]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchEvents,
  } = useGetCommunityEventsQuery(queryParams);

  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

  useEffect(() => {
    if (typeof refetchEvents === 'function') {
      refetchEvents();
    }
  }, [debouncedValue, refetchEvents]);

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  const rows: IEventData[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data;
  }, [data]);

  const handleCreateEvent = useCallback(() => {
    navigate('/event/create', {
      state: { from: `${location.pathname}${location.search}` },
    });
  }, [navigate, location.pathname, location.search]);

  const handleEditEvent = useCallback(
    (event: IEventData) => {
      navigate(`/event/edit/${event.id}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search]
  );

  const handleViewEvent = useCallback(
    (event: IEventData) => {
      navigate(`/event/edit/${event.id}?mode=view`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search]
  );

  const handleDeleteEvent = useCallback((event: IEventData) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!eventToDelete?.id || !communityId) return;

    try {
      await deleteEvent({
        communityId,
        eventId: eventToDelete.id,
      }).unwrap();

      enqueueSnackbar('Event deleted successfully', {
        variant: 'success',
      });
      handleCloseDeleteModal();
      refetchEvents();
    } catch (error) {
      let errorMessage = 'Failed to delete event';

      if (error && typeof error === 'object') {
        const rtkError = error as { data?: unknown; message?: string };

        if (
          rtkError.data &&
          typeof rtkError.data === 'object' &&
          rtkError.data !== null
        ) {
          const errorData = rtkError.data as {
            data?: { message?: string };
            message?: string;
          };

          if (
            errorData.data &&
            typeof errorData.data === 'object' &&
            'message' in errorData.data &&
            typeof errorData.data.message === 'string'
          ) {
            errorMessage = errorData.data.message;
          } else if (
            'message' in errorData &&
            typeof errorData.message === 'string'
          ) {
            errorMessage = errorData.message;
          }
        } else if (
          'message' in rtkError &&
          typeof rtkError.message === 'string'
        ) {
          errorMessage = rtkError.message;
        }
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [
    eventToDelete,
    communityId,
    deleteEvent,
    enqueueSnackbar,
    handleCloseDeleteModal,
    refetchEvents,
  ]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IEventData) => row.banner_images,
        id: 'banner_image',
        header: 'Image',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => {
          const bannerImages = row.original.banner_images;
          const firstImage =
            bannerImages && bannerImages.length > 0 ? bannerImages[0] : null;

          if (firstImage) {
            return (
              <div className="flex items-center">
                <img
                  src={firstImage}
                  alt={row.original.title}
                  className="w-12 h-12 object-cover rounded border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-image.png';
                  }}
                />
              </div>
            );
          }

          return (
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
                <KeenIcon icon="image" className="text-gray-400 text-base" />
              </div>
            </div>
          );
        },
      },
      {
        accessor: (row: IEventData) => row.title,
        id: 'title',
        header: 'Title',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span
            className="font-medium block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.title}
          >
            {row.original.title}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.description,
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span
            className="text-gray-800 font-normal block"
            style={{
              minWidth: 0,
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.description}
          >
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.location_text,
        id: 'location',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.location_text || '-'}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.start_at,
        id: 'start_date',
        header: 'Start Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDate(row.original.start_at)}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.end_at,
        id: 'end_date',
        header: 'End Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDate(row.original.end_at)}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.total_interested ?? 0,
        id: 'total_interested',
        header: 'Total Interested',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <span className="text-gray-800 font-normal">
            {typeof row.original.total_interested === 'number'
              ? row.original.total_interested
              : 0}
          </span>
        ),
      },
      {
        accessor: (row: IEventData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) =>
          getStatusBadge(row.original.status),
      },
      {
        accessor: (row: IEventData) => row.id,
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IEventData } }) => (
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() => handleViewEvent(row.original)}
              title="View"
            >
              <KeenIcon icon="eye" className="text-base" />
            </button>
            {hasPermission('community_event_promotion_update') &&
              row.original.status.toLowerCase() !== 'archived' && (
                <button
                  className="btn btn-sm btn-icon btn-light"
                  onClick={() => handleEditEvent(row.original)}
                  title="Edit"
                >
                  <KeenIcon icon="pencil" className="text-base" />
                </button>
              )}
            {hasPermission('community_event_promotion_delete') && (
              <button
                className="btn btn-sm btn-icon btn-light btn-outline"
                onClick={() => handleDeleteEvent(row.original)}
                title="Delete"
              >
                <KeenIcon type="filled" icon="trash" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [handleEditEvent, handleViewEvent, handleDeleteEvent, hasPermission]
  );

  return (
    <>
      <div className="card card-grid w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0" role="search">
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center gap-2"
                  htmlFor="search-events"
                  aria-label="Search events"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                    aria-hidden="true"
                  />
                  <input
                    id="search-events"
                    type="text"
                    placeholder="Search events..."
                    className="w-full bg-transparent border-0 outline-none focus:ring-0"
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search events"
                  />
                </label>
              </div>
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={resetFilters}
                  aria-label="Reset search filters"
                >
                  Reset
                </button>
              )}
            </div>
            {hasPermission('community_event_promotion_create') && (
              <button
                onClick={handleCreateEvent}
                className="btn btn-light"
              >
                Create Event
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div
              className="text-center py-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Loading events...
            </div>
          ) : isError ? (
            <div
              className="text-center py-4 text-red-500"
              role="alert"
              aria-live="assertive"
            >
              Failed to load events. Please try again.
            </div>
          ) : (
            <div className="event-grid-wrapper">
              <DataGrid<IEventData>
                columns={columns}
                data={rows}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={!!searchTerm}
                aria-label="Event listings table"
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete Event"
        message={`Are you sure you want to delete the event "${eventToDelete?.title || ''}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        confirmButtonVariant="danger"
        disabled={isDeleting}
      />
    </>
  );
};

export default EventPromotion;
